import React, { useState, useEffect, useCallback } from 'react';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  onSnapshot, // For real-time updates on permissions doc
  Firestore,
} from 'firebase/firestore';
import { Functions, httpsCallable, HttpsCallableResult } from 'firebase/functions';
// Assuming useAuth and UserIcon are correctly pathed based on your project structure
import { useAuth } from '../../hooks/AuthProvider';
import UserIcon from '../authentication/UserIcon';
// You'll need to create or adapt a CSS file for this component
// import '../../styles/AdminPermissionManager.css';

// --- Interfaces (matching backend) ---
interface MetadataActionPermissions {
  [role: string]: string | string[]; // e.g., read: ["APP_ADMIN"], write: "ALLOW_ALL"
}

interface MetadataCollectionData {
  permissions: MetadataActionPermissions;
  fields: { id: string; name: string; permissions?: MetadataActionPermissions }[];
  lastUpdated?: number;
}

// For the new 'permissions' collection documents
interface PermissionsAssignment {
  [role: string]: string[]; // roleName: [userEmail1, userEmail2, ...]
}
interface DocumentPermissionsAssignment {
  [docId: string]: PermissionsAssignment;
}
interface GroupPermissionsData {
  collectionLevel: PermissionsAssignment;
  documentLevel: DocumentPermissionsAssignment;
  lastUpdated?: number;
}

const AdminPermissionManager: React.FC<{ db: Firestore; functions: Functions }> = ({ db, functions }) => {
  const { currentUser } = useAuth(); // For initial auth check if needed for UI rendering

  // --- Global State ---
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // --- Collection Metadata State ---
  const [collectionNames, setCollectionNames] = useState<string[]>([]);
  const [selectedCollectionName, setSelectedCollectionName] = useState<string>('');
  const [selectedCollectionMetadata, setSelectedCollectionMetadata] = useState<MetadataCollectionData | null>(null);

  // --- Group Permissions State ---
  const [selectedGroupPermissions, setSelectedGroupPermissions] = useState<GroupPermissionsData | null>(null);
  const [groupMgmtLevel, setGroupMgmtLevel] = useState<'collection' | 'document'>('collection');
  const [groupMgmtDocId, setGroupMgmtDocId] = useState<string>('');
  const [groupMgmtRole, setGroupMgmtRole] = useState<string>('');
  const [groupMgmtEmails, setGroupMgmtEmails] = useState<string>('');

  // --- Form States for Metadata Policy ---
  const [newCollectionFormName, setNewCollectionFormName] = useState<string>('');
  const [newCollectionMetadataPolicy, setNewCollectionMetadataPolicy] = useState<MetadataActionPermissions>({ read: 'ALLOW_ALL', write: ['APP_ADMIN', 'DEVELOPER'] });
  const [editCollectionMetadataPolicy, setEditCollectionMetadataPolicy] = useState<MetadataActionPermissions | null>(null);

  // --- Cloud Function References ---
  // Ensure the function names match your deployed Firebase Functions
  const createCollectionFunc = httpsCallable(functions, 'createCollection');
  const setCollectionMetadataPermissionsFunc = httpsCallable(functions, 'setCollectionMetadataPermissions');
  const setGroupPermissionsFunc = httpsCallable(functions, 'setGroupPermissions');

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  useEffect(() => {
    const loadNames = async () => {
      setLoading(true);
      clearMessages();
      try {
        const querySnapshot = await getDocs(collection(db, 'metadata'));
        setCollectionNames(querySnapshot.docs.map(d => d.id).sort());
      } catch (err: any) {
        setError('Failed to load collection names: ' + err.message);
        console.error('Failed to load collection names:', err);
      } finally {
        setLoading(false);
      }
    };
    if (currentUser) { // Only load if user is authenticated
        loadNames();
    }
  }, [db, currentUser]);

  useEffect(() => {
    if (!selectedCollectionName || !currentUser) {
      setSelectedCollectionMetadata(null);
      setSelectedGroupPermissions(null);
      setEditCollectionMetadataPolicy(null);
      return;
    }

    let unsubMetadata: (() => void) | null = null;
    let unsubGroupPerms: (() => void) | null = null;

    const loadData = async () => {
      setLoading(true);
      clearMessages();
      
      // Load metadata policy (can use onSnapshot too if it changes often by other admins)
      const metaDocRef = doc(db, 'metadata', selectedCollectionName);
      unsubMetadata = onSnapshot(metaDocRef, (metaSnap) => {
        if (metaSnap.exists()) {
          const data = metaSnap.data() as MetadataCollectionData;
          setSelectedCollectionMetadata(data);
          setEditCollectionMetadataPolicy(JSON.parse(JSON.stringify(data.permissions)));
        } else {
          setError(`Metadata for ${selectedCollectionName} not found.`);
          setSelectedCollectionMetadata(null);
          setEditCollectionMetadataPolicy(null);
        }
      }, (err: any) => {
        setError(`Failed to load metadata for ${selectedCollectionName}: ` + err.message);
        console.error(`Failed to load metadata for ${selectedCollectionName}:`, err);
        setSelectedCollectionMetadata(null);
        setEditCollectionMetadataPolicy(null);
      });

      // Load/Subscribe to group permissions document
      const groupPermDocRef = doc(db, 'permissions', selectedCollectionName);
      unsubGroupPerms = onSnapshot(groupPermDocRef, (snap) => {
        if (snap.exists()) {
          setSelectedGroupPermissions(snap.data() as GroupPermissionsData);
        } else {
          setSelectedGroupPermissions({ collectionLevel: {}, documentLevel: {}, lastUpdated: Date.now() });
        }
      }, (err: any) => {
        setError(`Failed to load group permissions for ${selectedCollectionName}: ` + err.message);
        console.error(`Failed to load group permissions for ${selectedCollectionName}:`, err);
        setSelectedGroupPermissions(null);
      });
      
      setLoading(false); // Initial load done, snapshot will handle updates
    };

    loadData();

    return () => { // Cleanup subscriptions
      if (unsubMetadata) unsubMetadata();
      if (unsubGroupPerms) unsubGroupPerms();
    };
  }, [db, selectedCollectionName, currentUser]);


  const handleCreateCollection = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCollectionFormName) {
      setError("Collection name is required.");
      return;
    }
    setLoading(true);
    clearMessages();
    try {
      const result = await createCollectionFunc({
        name: newCollectionFormName,
        permissions: newCollectionMetadataPolicy,
      }) as HttpsCallableResult<{success: boolean; message: string}>;

      if (result.data.success) {
        setSuccess(result.data.message);
        // collectionNames will update via effect if metadata collection changes, or manually add:
        setCollectionNames(prev => [...prev, newCollectionFormName].sort());
        setNewCollectionFormName('');
        setNewCollectionMetadataPolicy({ read: 'ALLOW_ALL', write: ['APP_ADMIN', 'DEVELOPER'] });
      } else {
        setError(result.data.message || 'Failed to create collection.');
      }
    } catch (err: any) {
      setError('Error creating collection: ' + (err.message || 'Unknown error'));
      console.error('Error creating collection:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCollectionMetadataPolicy = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCollectionName || !editCollectionMetadataPolicy) {
      setError("No collection selected or policy data missing for update.");
      return;
    }
    setLoading(true);
    clearMessages();
    try {
      const result = await setCollectionMetadataPermissionsFunc({
        name: selectedCollectionName,
        metadataPolicy: editCollectionMetadataPolicy,
      }) as HttpsCallableResult<{success: boolean; message: string}>;

      if (result.data.success) {
        setSuccess(result.data.message);
        // Data will refresh via onSnapshot for selectedCollectionMetadata
      } else {
        setError(result.data.message || 'Failed to update metadata policy.');
      }
    } catch (err: any) {
      setError('Error updating metadata policy: ' + (err.message || 'Unknown error'));
      console.error('Error updating metadata policy:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetGroupPermission = async (operation: 'grant' | 'revoke') => {
    if (!selectedCollectionName || !groupMgmtRole || !groupMgmtEmails) {
      setError("Selected Collection, Role, and Emails are required for group permissions.");
      return;
    }
    if (groupMgmtLevel === 'document' && !groupMgmtDocId) {
      setError("Document ID is required for document-level group permissions.");
      return;
    }

    setLoading(true);
    clearMessages();
    const emailsArray = groupMgmtEmails.split(/[\s,;]+/).map(e => e.trim()).filter(e => e && e.includes('@'));
    
    if (emailsArray.length === 0) {
        setError("No valid emails provided. Please enter valid email addresses separated by commas, spaces, or semicolons.");
        setLoading(false);
        return;
    }

    try {
      const result = await setGroupPermissionsFunc({
        collectionName: selectedCollectionName,
        level: groupMgmtLevel,
        docId: groupMgmtLevel === 'document' ? groupMgmtDocId : undefined,
        role: groupMgmtRole.trim(),
        emails: emailsArray,
        operation: operation,
      }) as HttpsCallableResult<{success: boolean; message: string; userPermissionString?: string}>;

      if (result.data.success) {
        setSuccess(result.data.message);
        setGroupMgmtEmails(''); // Clear emails after successful operation
        // selectedGroupPermissions will update via onSnapshot
      } else {
        setError(result.data.message || `Failed to ${operation} group permissions.`);
      }
    } catch (err: any) {
      setError(`Error ${operation}ing group permissions: ` + (err.message || 'Unknown error'));
      console.error(`Error ${operation}ing group permissions:`, err);
    } finally {
      setLoading(false);
    }
  };

  const renderMetadataPolicyEditor = (
    policy: MetadataActionPermissions,
    setPolicy: React.Dispatch<React.SetStateAction<MetadataActionPermissions | null>> // Corrected Type
  ) => {
    const handlePolicyChange = (roleKey: string, value: string) => {
      if (!policy) return; // Should not happen if guarded by caller
      const updatedPolicy = { ...policy };

      if (value.toUpperCase() === 'ALLOW_ALL' || value.toUpperCase() === 'ALLOW_NONE') {
        updatedPolicy[roleKey] = value.toUpperCase();
      } else {
        const rolesArray = value.split(',').map(s => s.trim()).filter(s => s);
        updatedPolicy[roleKey] = rolesArray.length > 0 ? rolesArray : []; // Use empty array if all roles removed
      }
      setPolicy(updatedPolicy);
    };
    
    // Example for 'read' and 'write', you can make this dynamic
    const definedRoles = ['read', 'write']; // Add other roles you typically define in metadata policies

    return (
      <>
        {definedRoles.map(roleKey => (
          <div className="form-group" key={roleKey}>
            <label>Metadata Policy for "{roleKey}" (e.g., ALLOW_ALL, APP_ADMIN,EDITOR_ROLE)</label>
            <input
              type="text"
              className="form-input"
              placeholder="ALLOW_ALL or comma-separated roles"
              value={Array.isArray(policy[roleKey]) ? (policy[roleKey] as string[]).join(',') : (policy[roleKey] || '')}
              onChange={(e) => handlePolicyChange(roleKey, e.target.value)}
            />
          </div>
        ))}
      </>
    );
  };

  const renderCurrentMetadataPolicy = (policy: MetadataActionPermissions | undefined) => {
    if (!policy || Object.keys(policy).length === 0) return <p className="muted-text">No specific metadata policy set or loaded for this collection.</p>;
    return (
      <div className="info-box">
        {Object.entries(policy).map(([role, value]) => (
          <p key={role}>
            <strong>{role}:</strong> {Array.isArray(value) ? value.join(', ') : value}
          </p>
        ))}
      </div>
    );
  };

  const renderCurrentGroupAssignments = () => {
    if (!selectedGroupPermissions || !selectedCollectionName || !groupMgmtRole.trim()) {
        return <p className="muted-text">Select a collection and specify a role to see current assignments.</p>;
    }

    const roleKey = groupMgmtRole.trim();
    let emailsForRole: string[] = [];

    if (groupMgmtLevel === 'collection') {
        emailsForRole = selectedGroupPermissions.collectionLevel?.[roleKey] || [];
    } else if (groupMgmtDocId.trim()) {
        emailsForRole = selectedGroupPermissions.documentLevel?.[groupMgmtDocId.trim()]?.[roleKey] || [];
    } else if (groupMgmtLevel === 'document' && !groupMgmtDocId.trim()){
        return <p className="muted-text">Enter a Document ID to see document-level assignments.</p>;
    }


    if (emailsForRole.length === 0) {
        return <p className="muted-text">No users assigned to the role: <strong>{roleKey}</strong> for {groupMgmtLevel === 'document' && groupMgmtDocId.trim() ? `document '${groupMgmtDocId.trim()}'` : 'the collection'}.</p>;
    }

    return (
        <div className="info-box assignments-box">
            <p>Users with role <strong>{roleKey}</strong> for {groupMgmtLevel === 'document' && groupMgmtDocId.trim() ? `document '${groupMgmtDocId.trim()}'` : 'the collection'}:</p>
            <ul className="assignments-list">
                {emailsForRole.map(email => <li key={email}>{email}</li>)}
            </ul>
        </div>
    );
  };

  if (!currentUser) {
    return <div className="centered-message">Please log in to access the admin panel.</div>;
  }

  return (
    <div className="admin-permission-manager">
      <style>{`
        .admin-permission-manager { font-family: sans-serif; padding: 20px; max-width: 1200px; margin: auto; }
        .panel-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #eee; padding-bottom: 10px; margin-bottom: 20px; }
        .panel-header h1 { margin: 0; font-size: 1.8em; }
        .error-alert, .success-alert { padding: 10px; margin-bottom: 15px; border-radius: 4px; cursor: pointer; }
        .error-alert { background-color: #f8d7da; color: #721c24; border: 1px solid #f5c6cb; }
        .success-alert { background-color: #d4edda; color: #155724; border: 1px solid #c3e6cb; }
        .loading-indicator { padding: 10px; background-color: #e2e3e5; text-align: center; margin-bottom: 15px; }
        .panel-layout { display: grid; grid-template-columns: 1fr 1px 1fr; gap: 20px; }
        .panel-section { border: 1px solid #ddd; padding: 15px; border-radius: 5px; background-color: #f9f9f9; }
        .panel-section h2 { margin-top: 0; border-bottom: 1px solid #eee; padding-bottom: 8px; font-size: 1.5em; }
        .panel-section h3 { margin-top: 20px; margin-bottom: 10px; font-size: 1.2em; }
        .panel-section h4 { margin-top: 15px; margin-bottom: 8px; font-size: 1.1em; }
        .form-container { margin-bottom: 20px; }
        .form-group { margin-bottom: 15px; }
        .form-group label { display: block; margin-bottom: 5px; font-weight: bold; }
        .form-input, .form-select, .form-textarea { width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box; }
        .form-textarea { resize: vertical; }
        .primary-button, .success-button, .danger-button { padding: 10px 15px; border: none; border-radius: 4px; color: white; cursor: pointer; font-size: 1em; }
        .primary-button { background-color: #007bff; }
        .primary-button:hover { background-color: #0056b3; }
        .success-button { background-color: #28a745; margin-right: 10px; }
        .success-button:hover { background-color: #1e7e34; }
        .danger-button { background-color: #dc3545; }
        .danger-button:hover { background-color: #b02a37; }
        .button-group { margin-top: 10px; }
        .muted-text { color: #6c757d; }
        .info-box { background-color: #e9ecef; padding: 10px; border-radius: 4px; margin-top: 10px; }
        .info-box p { margin: 5px 0; }
        .info-box strong { color: #333; }
        .assignments-box { max-height: 200px; overflow-y: auto; }
        .assignments-list { list-style: none; padding-left: 0; }
        .assignments-list li { padding: 3px 0; border-bottom: 1px dashed #ddd; }
        .assignments-list li:last-child { border-bottom: none; }
        .vertical-divider { background-color: #ddd; }
        .centered-message { text-align: center; padding: 50px; font-size: 1.2em; color: #6c757d; }
        hr { border: none; border-top: 1px solid #eee; margin: 20px 0; }
        /* Basic responsive for smaller screens */
        @media (max-width: 900px) {
          .panel-layout { grid-template-columns: 1fr; }
          .vertical-divider { display: none; }
        }
      `}</style>

      <div className="panel-header">
        <h1>Permissions Admin Panel</h1>
        {currentUser && <UserIcon />}
      </div>

      {error && <div className="error-alert" onClick={() => setError('')} role="alert">{error}</div>}
      {success && <div className="success-alert" onClick={() => setSuccess('')} role="alert">{success}</div>}
      {loading && <div className="loading-indicator">Loading data, please wait...</div>}

      <div className="panel-layout">
        <section className="panel-section">
          <h2>Collection Metadata Policies</h2>
          <form onSubmit={handleCreateCollection} className="form-container">
            <h3>Create New Collection</h3>
            <div className="form-group">
              <label htmlFor="newCollectionName">Collection Name:</label>
              <input id="newCollectionName" type="text" value={newCollectionFormName} onChange={(e) => setNewCollectionFormName(e.target.value)} required className="form-input"/>
            </div>
            {renderMetadataPolicyEditor(newCollectionMetadataPolicy, setNewCollectionMetadataPolicy as React.Dispatch<React.SetStateAction<MetadataActionPermissions | null>>)}
            <button type="submit" disabled={loading} className="primary-button">Create Collection</button>
          </form>
          <hr />
          <h3>Manage Existing Collection Metadata</h3>
          <div className="form-group">
            <label htmlFor="selectCollectionMeta">Select Collection:</label>
            <select id="selectCollectionMeta" value={selectedCollectionName} onChange={(e) => setSelectedCollectionName(e.target.value)} className="form-select">
              <option value="">-- Select a Collection --</option>
              {collectionNames.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>

          {selectedCollectionName && editCollectionMetadataPolicy && (
            <form onSubmit={handleUpdateCollectionMetadataPolicy} className="form-container" style={{marginTop: '10px'}}>
              <h4>Edit Metadata Policy for: <strong>{selectedCollectionName}</strong></h4>
              {renderMetadataPolicyEditor(editCollectionMetadataPolicy, setEditCollectionMetadataPolicy)}
              <button type="submit" disabled={loading} className="primary-button">Update Metadata Policy</button>
              <h5 style={{marginTop: '15px'}}>Current Metadata Policy:</h5>
              {selectedCollectionMetadata && renderCurrentMetadataPolicy(selectedCollectionMetadata.permissions)}
            </form>
          )}
           {!selectedCollectionName && <p className="muted-text">Select a collection to view or edit its metadata policy.</p>}
        </section>

        <div className="vertical-divider"></div>

        <section className="panel-section">
          <h2>Group Permissions (User Role Assignments)</h2>
          {!selectedCollectionName ? (
            <p className="muted-text">Select a collection from the left to manage its group permissions.</p>
          ) : (
            <>
              <h3>Manage for Collection: <strong>{selectedCollectionName}</strong></h3>
              <div className="form-group">
                <label htmlFor="groupMgmtLevel">Level:</label>
                <select id="groupMgmtLevel" value={groupMgmtLevel} onChange={(e) => setGroupMgmtLevel(e.target.value as 'collection' | 'document')} className="form-select">
                  <option value="collection">Collection Level</option>
                  <option value="document">Document Level</option>
                </select>
              </div>
              {groupMgmtLevel === 'document' && (
                <div className="form-group">
                  <label htmlFor="groupMgmtDocId">Document ID:</label>
                  <input id="groupMgmtDocId" type="text" value={groupMgmtDocId} onChange={(e) => setGroupMgmtDocId(e.target.value)} className="form-input" placeholder="Enter Document ID"/>
                </div>
              )}
              <div className="form-group">
                <label htmlFor="groupMgmtRole">Role (e.g., read, write, chat):</label>
                <input id="groupMgmtRole" type="text" value={groupMgmtRole} onChange={(e) => setGroupMgmtRole(e.target.value.toLowerCase())} className="form-input" placeholder="Enter role name"/>
              </div>
              <div className="form-group">
                <label htmlFor="groupMgmtEmails">User Emails (comma, space, or semicolon separated):</label>
                <textarea id="groupMgmtEmails" value={groupMgmtEmails} onChange={(e) => setGroupMgmtEmails(e.target.value)} className="form-textarea" rows={3} placeholder="user1@example.com, user2@example.com"></textarea>
              </div>
              <div className="button-group">
                <button onClick={() => handleSetGroupPermission('grant')} disabled={loading || !groupMgmtRole.trim() || !selectedCollectionName} className="success-button">Grant Role</button>
                <button onClick={() => handleSetGroupPermission('revoke')} disabled={loading || !groupMgmtRole.trim() || !selectedCollectionName} className="danger-button">Revoke Role</button>
              </div>
              <hr/>
              <h4>Current Assignments for '{groupMgmtRole.trim() || "..."}'</h4>
              {renderCurrentGroupAssignments()}
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminPermissionManager;